/**
 * Maps the PLUs /clientTransaction refused back onto the cart lines that carried them.
 *
 * A refused product can sit anywhere in a line: it can be the line itself, a chosen
 * add-on on it, or a product inside a package. A package is not one cart line — it is a
 * parent line holding `groupId` plus sibling child lines whose `parentGroupId` points at
 * it (see addToCart in CartContext), and the POS cannot sell one with a component
 * missing. So a hit anywhere inside a package rejects the whole package, and the cashier
 * is told which product caused it: "Package X is unavailable because Product Y is sold
 * out" rather than just that Y is gone.
 */

/**
 * The PLU an order line was sent under: for a variant product that is the chosen
 * variant's, not the parent's — the same resolution sendTransaction does when it builds
 * order_items, so what the server named is what gets matched here.
 */
export const orderedPluOf = (item) => {
  if (item.is_variant) {
    return item.variant_item?.find((variant) => variant.checked)?.plu_number;
  }
  return item.plu_number;
};

/** The name that line was ordered under, for the same reason. */
const orderedNameOf = (item) => {
  if (item.is_variant) {
    const variant = item.variant_item?.find((entry) => entry.checked);
    if (variant) return variant.plu_name;
  }
  return item.plu_name;
};

/**
 * Everything chosen on a line that is sent as a prep item: `group.item` when the group
 * carries an `_id`, `group.prep_item` otherwise — the same two sources order_items reads.
 * Products chosen inside a group are not here; they became their own cart lines.
 */
const chosenPrepItems = (item) =>
  (item.group ?? []).flatMap((group) => [
    ...(group.prep_item ?? []),
    ...(group.item ?? []),
  ]);

/**
 * The products bundled inside a package, flattened. A package's `sub_product` is a fixed
 * bill of materials — RePackageItem displays it read-only, there is nothing to choose —
 * so every entry is part of what was ordered, and one of them selling out makes the
 * package unsellable. Choosable components are separate cart lines instead.
 *
 * The server checks these too (fe-lambda 0.5.20). They must be matched here as well, or a
 * refusal naming one would belong to no cart line: the customer would be told to remove
 * something, nothing would go, and confirming again would be refused again.
 */
const bundledProducts = (item, depth = 0) =>
  depth > 4 || !item?.sub_product?.length
    ? []
    : item.sub_product.flatMap((sub) => [sub, ...bundledProducts(sub, depth + 1)]);

/**
 * @param cartItems the cart as sent
 * @param refusedItems what the server returned: [{ plu_number, plu_name, reason }]
 * @returns {{ entries: Array, keep: Array }} entries to show the customer, and the cart
 *   lines that survive. `entries` holds one row per package or standalone product, each
 *   `{ key, name, reason, cause }`, where `cause` names the product that poisoned a
 *   package and is null when the named thing is itself the refused one.
 */
export function resolveUnavailable(cartItems = [], refusedItems = []) {
  const reasonOf = new Map(
    refusedItems
      .filter((item) => item && item.plu_number)
      .map((item) => [item.plu_number, item]),
  );

  if (reasonOf.size === 0) {
    return { entries: [], keep: [...cartItems] };
  }

  // What was hit on each line, and the refused PLUs that turned out to be in the cart.
  // A line can be hit more than once: several of a package's components can sell out at
  // the same time, and the cashier has to explain all of them, not the first one found.
  const matched = new Set();
  const selfHit = new Set(); // indexes whose own ordered PLU was refused
  const prepHits = new Map(); // index -> the refused prep items chosen on that line
  const bundleHits = new Map(); // index -> the refused products bundled in that package

  cartItems.forEach((item, index) => {
    const own = orderedPluOf(item);
    if (own && reasonOf.has(own)) {
      matched.add(own);
      selfHit.add(index);
    }
    const preps = chosenPrepItems(item).filter(
      (entry) => entry.plu_number && reasonOf.has(entry.plu_number),
    );
    if (preps.length > 0) {
      preps.forEach((prep) => matched.add(prep.plu_number));
      prepHits.set(
        index,
        preps.map((prep) => reasonOf.get(prep.plu_number)),
      );
    }

    const bundled = bundledProducts(item).filter(
      (entry) => entry.plu_number && reasonOf.has(entry.plu_number),
    );
    if (bundled.length > 0) {
      bundled.forEach((entry) => matched.add(entry.plu_number));
      bundleHits.set(
        index,
        bundled.map((entry) => reasonOf.get(entry.plu_number)),
      );
    }
  });

  const hitLines = new Set([...selfHit, ...prepHits.keys(), ...bundleHits.keys()]);

  const parentLineOf = (groupId) =>
    groupId ? cartItems.find((item) => item.groupId === groupId) : undefined;

  /**
   * The groups a line belongs to, innermost first: its own, then every package
   * containing it.
   *
   * Today that chain is at most one long — `addToCart` assigns `groupId` only to the
   * product being added and `parentGroupId` only to the products chosen inside it, and
   * never recurses, so no line carries both. Walking the chain anyway means a package
   * nested inside a package would be handled rather than silently surviving its
   * container's removal, which is the same invalid state one level down.
   */
  const groupsOf = (item) => {
    const chain = [];
    if (item.groupId) chain.push(item.groupId);
    let parent = item.parentGroupId;
    while (parent && !chain.includes(parent)) {
      chain.push(parent);
      parent = parentLineOf(parent)?.parentGroupId;
    }
    return chain;
  };

  // A hit anywhere in a package condemns the package: the parent, every sibling, and
  // every package containing it — a container holding an invalid package is invalid too.
  const condemnedGroups = new Set();
  hitLines.forEach((index) => {
    groupsOf(cartItems[index]).forEach((groupId) => condemnedGroups.add(groupId));
  });

  const isRejected = (item, index) =>
    hitLines.has(index) || groupsOf(item).some((id) => condemnedGroups.has(id));

  const keep = cartItems.filter((item, index) => !isRejected(item, index));

  // One row per package, one per standalone product.
  const entries = [];
  const seen = new Set();

  /**
   * Everything refused on one line, as causes for the row being built. `on` names the
   * component a refused add-on was chosen on, so "Cheese is sold out" inside a package
   * can say which part of it carried the cheese; it is null when that would just repeat
   * the row's own name.
   */
  const causesOn = ({ item, index }, rowName) => {
    const causes = [];
    const lineName = orderedNameOf(item);
    if (selfHit.has(index) && lineName !== rowName) {
      const refusal = reasonOf.get(orderedPluOf(item));
      causes.push({ plu_number: refusal.plu_number, plu_name: lineName, reason: refusal.reason, on: null });
    }
    [...(prepHits.get(index) ?? []), ...(bundleHits.get(index) ?? [])].forEach((cause) => {
      causes.push({ ...cause, on: lineName === rowName ? null : lineName });
    });
    return causes;
  };

  condemnedGroups.forEach((groupId) => {
    // Name the outermost condemned package only: an inner one is already covered by the
    // row for its container, and listing both would ask the customer to remove the same
    // thing twice.
    const container = parentLineOf(groupId)?.parentGroupId;
    if (container && condemnedGroups.has(container)) return;

    const lines = cartItems
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => groupsOf(item).includes(groupId));

    const parent = lines.find(({ item }) => item.groupId === groupId) ?? lines[0];
    const name = orderedNameOf(parent.item);

    // The package's own product being refused is the package's own reason; everything
    // else is explained by its causes, all of them.
    const own = selfHit.has(parent.index)
      ? reasonOf.get(orderedPluOf(parent.item))
      : null;

    entries.push({
      key: `group-${groupId}`,
      name,
      reason: own?.reason ?? null,
      causes: lines.flatMap((line) => causesOn(line, name)),
    });
  });

  hitLines.forEach((index) => {
    const item = cartItems[index];
    // Already named as a package.
    if (groupsOf(item).some((id) => condemnedGroups.has(id))) return;

    const name = orderedNameOf(item);
    const causes = causesOn({ item, index }, name);
    const own = selfHit.has(index) ? reasonOf.get(orderedPluOf(item)) : null;
    const key = `item-${name}-${own?.reason ?? ''}-${causes.map((c) => c.plu_number).join(',')}`;
    if (seen.has(key)) return;
    seen.add(key);

    entries.push({ key, name, reason: own?.reason ?? null, causes });
  });

  // A PLU the server refused that is no longer anywhere in the cart: still say so, with
  // the server's own name, so the dialog can never come up empty.
  reasonOf.forEach((item, plu) => {
    if (matched.has(plu)) return;
    const key = `server-${plu}`;
    if (seen.has(key)) return;
    seen.add(key);
    entries.push({ key, name: item.plu_name, reason: item.reason, causes: [] });
  });

  return { entries, keep };
}
